import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useT } from '../../i18n';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import { Card, Message, ReportPage } from './ReportParts';
import { downloadReport } from './reportsApi';
import { countText, moneyExact, supplierName } from './reportFormat';
import { useReport } from './useReport';

// The result of a compared statement, straight after the upload and when reopened from Commissions: four groups, each with its lines and its total in the company currency.
//   Agrees / Amount or rate differs / On their statement, not in Floxir / Possible underpayment.
// Never silent: credit notes are listed, never subtracted; invoices of companies with no rate are counted apart, never summed; lines read from a scanned image are counted next to the
// total; lines that could not be read or converted are counted and left out of the totals.
const GROUPS = ['agrees', 'differs', 'not_in_floxir', 'not_on_statement'];
const SHOWN = 8;
const GROUP_COLOR = { agrees: L.green, differs: L.accent, not_in_floxir: L.accent, not_on_statement: L.accent };
const dayText = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const one = (t, base, n, vars = {}) => t(n === 1 ? `${base}.one` : `${base}.many`, { n: countText(n), ...vars });

function Flags({ flags, fromImage, t }) {
  const list = [...(flags || []), ...(fromImage ? ['image'] : [])];
  if (!list.length) return null;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
      {list.map((f) => (
        <span key={f} data-testid={`flag-${f}`} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10, background: L.iconBg, color: L.text }}>{t(`reports.stmt.flag.${f}`)}</span>
      ))}
    </span>
  );
}

const Pair = ({ label, theirs, ours, t }) => (
  <span style={{ fontSize: 13, color: L.muted }}>{label}: {t('reports.stmt.theirs')} {theirs} · {t('reports.stmt.ours')} {ours}</span>
);

function Line({ line, group, currency, t, phone }) {
  const cur = line.currency || currency;
  const m = (v) => moneyExact(v, cur);
  const rate = (v) => (v == null ? '—' : `${v}%`);
  const left = (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{line.invoice_number}</span>
      <Flags flags={line.flags} fromImage={line.from_image} t={t} />
    </span>
  );
  let right;
  if (group === 'differs') {
    right = (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: phone ? 'flex-start' : 'flex-end' }}>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          <Pair label={t('reports.stmt.commission')} theirs={m(line.commission)} ours={m(line.our_commission)} t={t} />
        </span>
        {line.diff != null && <strong data-testid="line-diff" style={{ fontVariantNumeric: 'tabular-nums', color: L.accent }}>{t('reports.stmt.diff')} {line.diff > 0 ? '+' : ''}{m(line.diff)}</strong>}
        {(line.amount != null || line.our_amount != null) && <Pair label={t('reports.stmt.net')} theirs={m(line.amount)} ours={m(line.our_amount)} t={t} />}
        {(line.rate != null || line.our_rate != null) && <Pair label={t('reports.stmt.rate')} theirs={rate(line.rate)} ours={rate(line.our_rate)} t={t} />}
      </span>
    );
  } else if (group === 'not_on_statement') {
    right = (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: phone ? 'flex-start' : 'flex-end', fontVariantNumeric: 'tabular-nums' }}>
        <strong>{m(line.our_commission)}</strong>
        <span style={{ fontSize: 13, color: L.muted }}>{t('reports.stmt.termEnd')} {dayText(line.details && line.details.term_end)}</span>
      </span>
    );
  } else {
    right = <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{m(line.commission)}</strong>;
  }
  return (
    <div data-testid={`line-${group}`} style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'minmax(0, 1.2fr) minmax(0, 1.6fr)', gap: phone ? 6 : 14, padding: '10px 0', borderTop: `1px solid ${L.rowLine}`, fontSize: 15, alignItems: 'start', textAlign: phone ? 'left' : undefined }}>
      {left}
      <span style={{ textAlign: phone ? 'left' : 'right' }}>{right}</span>
    </div>
  );
}

function GroupCard({ group, stats, lines, noRate, base, currency, t, phone }) {
  const [all, setAll] = useState(false);
  const [showNoRate, setShowNoRate] = useState(false);
  const shown = all ? lines : lines.slice(0, SHOWN);
  const m = (v) => moneyExact(v, base);
  const total = group === 'differs' ? t('reports.stmt.total.differs', { theirs: m(stats.theirs_base), ours: m(stats.ours_base), diff: m(stats.diff_base) })
    : `${t(`reports.stmt.total.${group}`)}: ${m(group === 'not_on_statement' ? stats.ours_base : stats.theirs_base)}`;
  return (
    <Card testid={`group-${group}`} style={{ borderTop: `4px solid ${GROUP_COLOR[group]}` }}>
      <h2 style={{ margin: 0, fontFamily: L.serif, fontSize: 21, fontWeight: 500 }}>{t(`reports.stmt.group.${group}`)}</h2>
      <p style={{ margin: '4px 0 10px', fontSize: 14, color: L.muted }}>{t(`reports.stmt.group.${group}.hint`)}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
        <span data-testid={`group-count-${group}`} style={{ fontSize: 15, color: L.muted }}>{one(t, 'reports.stmt.lines', stats.count)}</span>
        <strong data-testid={`group-total-${group}`} style={{ fontFamily: L.serif, fontSize: 24, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{total}</strong>
        {stats.from_image > 0 && <span data-testid={`group-image-${group}`} style={{ fontSize: 13, color: L.accent }}>{t('reports.stmt.fromImage', { n: countText(stats.from_image) })}</span>}
        {stats.credits > 0 && <span style={{ fontSize: 13, color: L.muted }}>{one(t, 'reports.stmt.credits', stats.credits)}</span>}
        {stats.credits_base > 0 && <span data-testid={`group-credits-${group}`} style={{ fontSize: 13, color: L.muted }}>{t('reports.stmt.creditsReduce', { amount: moneyExact(stats.credits_base, currency) })}</span>}
        {stats.unconverted > 0 && <span style={{ fontSize: 13, color: L.muted }}>{one(t, 'reports.stmt.unconverted', stats.unconverted)}</span>}
      </div>
      {!lines.length ? <div style={{ color: L.muted, padding: '8px 0' }}>{t('reports.stmt.empty')}</div> : shown.map((l, i) => <Line key={`${l.line_no}-${i}`} line={l} group={group} currency={currency} t={t} phone={phone} />)}
      {lines.length > SHOWN && (
        <button type="button" onClick={() => setAll((v) => !v)} data-testid={`group-toggle-${group}`} style={{ margin: '10px 0 0', padding: 0, border: 'none', background: 'none', color: L.accent, font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          {all ? t('reports.showFewer') : t('reports.stmt.showAll', { n: countText(lines.length) })}
        </button>
      )}
      {group === 'not_on_statement' && noRate.length > 0 && (
        <div data-testid="group-no-rate" style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${L.border}`, fontSize: 14, color: L.muted }}>
          {one(t, 'reports.stmt.noRate', noRate.length)}
          <div>
            <button type="button" onClick={() => setShowNoRate((v) => !v)} data-testid="no-rate-toggle" style={{ margin: '6px 0 0', padding: 0, border: 'none', background: 'none', color: L.accent, font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{t('reports.stmt.noRate.show')}</button>
          </div>
          {showNoRate && noRate.map((l, i) => (
            <div key={`${l.invoice_id}-${i}`} data-testid="line-no-rate" style={{ padding: '8px 0', borderTop: `1px solid ${L.rowLine}`, color: L.text }}>
              <strong>{l.invoice_number}</strong> <span style={{ color: L.muted }}>· {t('reports.stmt.termEnd')} {dayText(l.details && l.details.term_end)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function StatementResultView() {
  const t = useT();
  const phone = useViewportKind() === 'phone';
  const { id } = useParams();
  const { status, data, retry } = useReport('statement-open', { id });
  const [failed, setFailed] = useState(false);

  const download = async () => {
    setFailed(false);
    const ok = await downloadReport('statement-file', { id }, 'statement');
    if (!ok) setFailed(true);
  };

  if (status !== 'ready') return <ReportPage title={t('reports.stmt.title')} backTo="/reports/commissions" testid="statement-result"><Message status={status} onRetry={retry} /></ReportPage>;
  const { statement: st, summary, lines } = data;
  const by = (g) => lines.filter((l) => l.grp === g);
  const base = summary.base || data.company_currency;
  const name = supplierName(st.supplier, t);
  return (
    <ReportPage title={`${t('reports.stmt.title')} · ${name}`} backTo="/reports/commissions" testid="statement-result">
      <Card testid="statement-head">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 28px', fontSize: 15 }}>
          <span><span style={{ color: L.muted }}>{t('reports.stmt.period')}: </span><strong>{dayText(st.period.from)} – {dayText(st.period.to)}</strong></span>
          <span style={{ overflowWrap: 'anywhere' }}><span style={{ color: L.muted }}>{t('reports.stmt.file')}: </span><strong>{st.file.name}</strong></span>
          <span><span style={{ color: L.muted }}>{t('reports.stmt.uploadedOn')}: </span><strong>{dayText(String(st.created_at).slice(0, 10))}</strong></span>
          <span>{one(t, 'reports.stmt.upload.lines', st.lines.read)}</span>
          {summary.printed_total != null && <span>{t('reports.stmt.printedTotal')}: <strong>{moneyExact(summary.printed_total, st.currency)}</strong></span>}
        </div>
        {st.lines.from_image > 0 && <p style={{ margin: '10px 0 0', fontSize: 14, color: L.accent }}>{t('reports.stmt.upload.fromImage', { n: countText(st.lines.from_image) })}</p>}
        <div style={{ marginTop: 14 }}>
          <button type="button" onClick={download} data-testid="statement-download" style={{ height: 40, padding: '0 18px', borderRadius: 20, border: `1px solid ${L.border}`, background: '#FFFFFF', color: L.text, font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{t('reports.stmt.download')}</button>
          {failed && <span role="alert" data-testid="statement-download-failed" style={{ marginLeft: 12, color: L.accent, fontSize: 14 }}>{t('reports.stmt.downloadFailed')}</span>}
        </div>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: 20, alignItems: 'start' }}>
        {GROUPS.map((g) => (
          <GroupCard key={g} group={g} stats={summary.groups[g]} lines={by(g)} noRate={g === 'not_on_statement' ? by('not_on_statement_no_rate') : []} base={base} currency={st.currency} t={t} phone={phone} />
        ))}
      </div>
      {summary.groups.unreadable.count > 0 && (
        <Card testid="group-unreadable" title={t('reports.stmt.unreadable.title')}>
          <p style={{ margin: '0 0 8px', fontSize: 14, color: L.muted }}>{one(t, 'reports.stmt.lines', summary.groups.unreadable.count)} · {t('reports.stmt.unreadable.hint')}</p>
          {by('unreadable').slice(0, 20).map((l, i) => (
            <div key={`${l.line_no}-${i}`} style={{ padding: '6px 0', borderTop: `1px solid ${L.rowLine}`, fontSize: 14, overflowWrap: 'anywhere' }}>
              <span style={{ color: L.muted }}>#{l.line_no}</span> {(l.details && l.details.text) || ''} <Flags flags={l.flags} t={t} />
            </div>
          ))}
        </Card>
      )}
    </ReportPage>
  );
}
